import { SectionTypes } from '@common/enums/section-type-enum';
import { ApplianceModel } from '@common/models';
import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/dist/query';

import { useFetchInStoreFaucetsQuery } from '../../../../../api/static-content-api/faucet-api/faucetService';

/**
 * Defining empty array outside of selector as indicated in docs.
 * @see {@link https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results}
 */
const emptyArray = [] satisfies ApplianceModel[];

export interface UseFaucetReturn {
    browsedFaucets: ApplianceModel[];
    isFetchingFaucets: boolean;
    isErrorFaucets: boolean;
}

const useFaucets = (): UseFaucetReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { isMaterialLoaded } = useMaterials();
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { isFetchingDependencies } = useFetchDependencies();
    const inStoreFaucetsUrl = useRetailerSettings().getInStoreApplianceUrl(SectionTypes.faucet);

    const {
        inStoreFaucets,
        isFetching: isFetchingInStoreFaucets,
        isError: isErrorInStoreFaucets,
    } = useFetchInStoreFaucetsQuery(
        isFetchingDependencies ||
            !inStoreFaucetsUrl ||
            !zipcode ||
            !selectedColor ||
            !selectedStore ||
            !isMaterialLoaded
            ? skipToken
            : { url: inStoreFaucetsUrl },
        {
            selectFromResult: ({ data, ...rest }) => ({
                inStoreFaucets: data && !rest.isError ? data : emptyArray,
                ...rest,
            }),
        }
    );

    return {
        browsedFaucets: inStoreFaucets, // Renamed to be consistent with other products
        isFetchingFaucets: isFetchingInStoreFaucets,
        isErrorFaucets: isErrorInStoreFaucets,
    };
};

export default useFaucets;
